import { useEffect, useRef, useState } from "react";
import keyboardInset, { typing } from "@/hooks/keyboardInset";

// Scroll every scrollable ancestor (vertically only) so the focused box sits just above the keys.
const reveal = (el, root, kb) => {
  const limit = window.innerHeight - kb - 16;
  for (let n = el.parentElement; n; n = n.parentElement) {
    if (n.scrollLeft) n.scrollLeft = 0;
    if (n.scrollHeight > n.clientHeight + 1) {
      const r = el.getBoundingClientRect();
      const top = Math.max(n.getBoundingClientRect().top + 12, 12);
      if (r.bottom > limit) n.scrollBy({ top: r.bottom - limit, behavior: "smooth" });
      else if (r.top < top) n.scrollBy({ top: r.top - top, behavior: "smooth" });
    }
    if (n === root) break;
  }
};

/**
 * Phones: the slide-up stays exactly where it is; while the keyboard is up it gains room at the
 * bottom and the tapped text box glides up to rest just above the keys. null otherwise.
 */
export default function useKeyboardLift(active, nodeRef) {
  const [style, setStyle] = useState(null);
  const height = useRef(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv || window.innerWidth >= 640) return undefined;
    // Pin the page behind where it was (iOS likes to scroll it when a box is tapped).
    const y = window.scrollY;
    let raf = 0;
    let lifted = false;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const node = nodeRef?.current;
        const kb = typing() && node?.contains(document.activeElement) ? keyboardInset() : 0;
        if (kb < 80) {
          lifted = false;
          setStyle(null);
          if (node) requestAnimationFrame(() => { height.current = node.offsetHeight; });
          return;
        }
        if (window.scrollY !== y) window.scrollTo(0, y);
        if (!lifted && node) height.current = node.offsetHeight || height.current;
        lifted = true;
        const h = height.current;
        setStyle({ ...(h ? { height: `${h}px`, maxHeight: `${h}px` } : {}), overflowY: "auto", paddingBottom: `${kb + 16}px` });
        setTimeout(() => reveal(document.activeElement, node, kb), 30);
      });
    };
    const onFocus = () => { update(); setTimeout(update, 120); setTimeout(update, 360); setTimeout(update, 700); };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("focusin", onFocus);
    window.addEventListener("focusout", onFocus);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("focusin", onFocus);
      window.removeEventListener("focusout", onFocus);
      setStyle(null);
    };
  }, [active, nodeRef]);
  return style;
}
